import { useToast } from "@chakra-ui/react";
import { Field, Form, FormikProvider, useFormik } from "formik";
import React from "react";
import validator from "validator";
import { useTurnstileContext } from "../../../providers/TurnstileProvider";
import { useSubscribeNewsletterMutation } from "../../../store/api/newsletter";
import Button from "../Button";
import FormControl from "../Input/FormControl";
import Input from "../Input/Input";
import Modal from "./Modal";
import Text from "../Text/Text";

export interface NewsletterModalProps {
  onClose: () => void;
}

const NewsletterModal = ({ onClose }: NewsletterModalProps) => {
  const [subscribe] = useSubscribeNewsletterMutation();
  const toast = useToast();
  const { consume } = useTurnstileContext();

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    async onSubmit(values, actions) {
      try {
        const token = consume();
        let recaptcha = undefined;

        if (token) {
          recaptcha = {
            token,
            action: "newsletter",
            type: "turnstile",
          };
        }

        const response = await subscribe({
          body: {
            email: values.email,
          },
          params: {
            recaptcha,
          },
        });

        if ("error" in response) {
          throw new Error("Failed to subscribe. Please try again later.");
        }

        toast({
          title: "Successfully Subscribed!",
          description: "Thank you for subscribing to our newsletter.",
          status: "success",
        });

        onClose();
      } catch (e: any) {
        toast({
          title: "An error occurred",
          description: e.message || "Failed to subscribe to newsletter",
          status: "error",
        });
      } finally {
        actions.setSubmitting(false);
        actions.resetForm();
      }
    },
  });

  return (
    <Modal isOpen={true} onClose={onClose}>
      <Modal.Overlay />

      <FormikProvider value={formik}>
        <Form style={{ display: "contents" }}>
          <Modal.Content m="4">
            <Modal.Header>Subscribe to our Newsletter</Modal.Header>
            <Modal.CloseButton />

            <Modal.Body display="flex" flexDir="column" gap="4">
              <Text>
                Stay up to date with the latest news, updates, and exclusive offers from MailBigFile!
              </Text>

              <FormControl
                variant="floating"
                isInvalid={!!(formik.touched.email && formik.errors.email)}
                error={<Text color="crimson">{formik.errors.email}</Text>}
              >
                <Field
                  as={Input}
                  id="email"
                  name="email"
                  type="email"
                  variant="filled"
                  label="Email"
                  placeholder=" "
                  validate={async (value: string) => {
                    if (!value) {
                      return "Please provide your email address";
                    }

                    if (!validator.isEmail(value)) {
                      return "Invalid email address";
                    }
                  }}
                />
              </FormControl>
            </Modal.Body>

            <Modal.Footer display="flex" gap="4">
              <Button type="submit" isProcessing={formik.isSubmitting}>
                Subscribe
              </Button>

              <Button onClick={onClose} isProcessing={formik.isSubmitting}>
                Close
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Form>
      </FormikProvider>
    </Modal>
  );
};

export default NewsletterModal;
